import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Menu, X } from 'lucide-react';


/**
 * Main header component with responsive navigation.
 * Includes scroll-aware styling and mobile menu functionality.
 *
 * @returns The header component with navigation.
 */
export const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out border-b ${isScrolled || mobileMenuOpen ? 'bg-obsidian/80 backdrop-blur-md border-white/5 py-4' : 'bg-transparent border-transparent py-6'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center relative z-50">
          <Link to="/" onClick={closeMenu} className="flex items-center gap-2 text-xl font-medium tracking-tight uppercase text-white group">
            <img src="/logo.svg" alt="Logo" className="w-12 h-12 opacity-80" />
            <span><span className="font-extrabold">KIVO</span>SYSTEMS</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex space-x-8 items-center text-sm font-medium tracking-wide text-gray-300">
            <Link to="/work" className="hover:text-white transition-colors">Work</Link>
            <Link to="/services" className="hover:text-white transition-colors">Services</Link>
            <Link to="/insights" className="hover:text-white transition-colors">Insights</Link>
            <Link to="/studio" className="hover:text-white transition-colors">About Us</Link>
            <Link to="/contact" className="px-5 py-2 border border-white/20 rounded-full hover:bg-white hover:text-obsidian transition-colors text-white">
              Work with Us
            </Link>
          </nav>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 -mr-2 text-white focus:outline-none"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Nav Overlay */}
      <div className={`fixed inset-0 bg-obsidian z-40 flex flex-col justify-center px-6 space-y-8 transition-all duration-300 pt-20 ${mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'}`}>
        <Link to="/" onClick={closeMenu} className="text-4xl font-display text-white hover:text-gray-300 transition-colors">Home</Link>
        <Link to="/work" onClick={closeMenu} className="text-4xl font-display text-white hover:text-gray-300 transition-colors">Work</Link>
        <Link to="/services" onClick={closeMenu} className="text-4xl font-display text-white hover:text-gray-300 transition-colors">Services</Link>
        <Link to="/insights" onClick={closeMenu} className="text-4xl font-display text-white hover:text-gray-300 transition-colors">Insights</Link>
        <Link to="/studio" onClick={closeMenu} className="text-4xl font-display text-white hover:text-gray-300 transition-colors">About Us</Link>
        <Link to="/contact" onClick={closeMenu} className="text-4xl font-display text-white hover:text-gray-300 transition-colors">Work with Us</Link>

        <div className="pt-12 border-t border-white/10 mt-8">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">Locations</p>
          <div className="flex flex-col space-y-4 text-lg text-gray-400">
            <Link to="/locations/london" onClick={closeMenu}>London</Link>
            <Link to="/locations/cyprus" onClick={closeMenu}>Cyprus</Link>
          </div>
        </div>
      </div>
    </>
  );
};
